// External
import React, { useMemo, useCallback, memo } from "react";
import { DataGrid } from "@toddle-edu/ds-data-grid";

// Hooks
import { useI18n } from "Hooks";
import useUploadSummary from "../../../hooks/useUploadSummary";

// Module
import { PAGINATION_BUFFER } from "../../../modules/BulkUploadConstants";
import {
  getReviewGridHeaders,
  getReviewGridData,
} from "../../../modules/BulkUploadHelpers";

const TEST_ID_PREFIX = "bulk-upload-review-data-grid";
const SKELETON_COLUMN_COUNT = 10;
const SKELETON_COLUMN_WIDTH = 160;

const SKELETON_HEADERS = Array.from(
  { length: SKELETON_COLUMN_COUNT },
  (_, i) => ({
    id: `skeleton-${i}`,
    title: "",
    width: SKELETON_COLUMN_WIDTH,
  })
);

const BulkUploadReviewDataGrid = ({ transactionId, operationType }) => {
  const { t } = useI18n();

  const {
    edges,
    headers,
    isLoading,
    isFetchingMoreData,
    hasNextPage,
    loadMoreFeed,
  } = useUploadSummary({ transactionId, operationType });

  const gridHeaders = useMemo(
    () =>
      isLoading && headers.length === 0
        ? SKELETON_HEADERS
        : getReviewGridHeaders({ headers, operationType, t }),
    [isLoading, headers, operationType, t]
  );

  const gridData = useMemo(
    () => getReviewGridData({ edges, headers, operationType, t }),
    [edges, headers, operationType, t]
  );

  const handlePaginateY = useCallback(() => {
    if (!isFetchingMoreData && hasNextPage && !isLoading) {
      loadMoreFeed();
    }
  }, [isFetchingMoreData, hasNextPage, isLoading, loadMoreFeed]);

  return (
    <DataGrid
      headers={gridHeaders}
      data={gridData}
      rowMarkers="none"
      dataGridHeight="100%"
      dataGridWidth="100%"
      paginationBuffer={PAGINATION_BUFFER}
      showSkeletonLoader={isLoading}
      isViewMode={true}
      showHoverState={false}
      roundedCorners={false}
      tooltipDelayTime={100}
      onPaginateY={handlePaginateY}
      testId={`${TEST_ID_PREFIX}-datagrid`}
    />
  );
};

export default memo(BulkUploadReviewDataGrid);
